import { Box, Stack } from '@mui/material';
import { useCallback, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';

import { Fullscreen } from '@/components/Fullscreen/Fullscreen';
import { useRequiredParam } from '@/hooks/useRequiredParam';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { FamilyTree } from '@/modules/familyTree';
import { EditFamilyTreeTrowser } from '@/modules/familyTree/components/EditFamilyTreeTrowser/EditFamilyTreeTrowser';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';

interface ClientFamilyTreeProps {
  householdId: string;
  unlinkedButtonPortalRef?: React.RefObject<HTMLDivElement>;
}

interface OutletContext {
  unlinkedButtonPortalRef?: React.RefObject<HTMLDivElement>;
}

const MIN_HEIGHT = 720;

export const ClientFamilyTreeInner = ({
  householdId,
  unlinkedButtonPortalRef,
}: ClientFamilyTreeProps) => {
  const canEdit = useCanEditHouseholdData();
  const editFamilyTreeTrowser = useTrowser(TrowserKey.EditFamilyTree);
  const isEditTrowserOpen = editFamilyTreeTrowser.isOpen;
  const [refreshKey, setRefreshKey] = useState(0);

  const { open: openEditTrowser, close: closeEditTrowser } =
    editFamilyTreeTrowser;
  const handleOpenEditTrowser = useCallback(() => {
    openEditTrowser();
  }, [openEditTrowser]);

  const handleCloseEditTrowser = useCallback(
    (didSave?: boolean) => {
      closeEditTrowser();
      if (didSave) {
        setRefreshKey((k) => k + 1);
      }
    },
    [closeEditTrowser]
  );

  return (
    <Stack flex={1} p={2} spacing={2}>
      {!isEditTrowserOpen && (
        <Fullscreen name="familyTree">
          <Box flex="1" minHeight={MIN_HEIGHT}>
            <AutoSizer>
              {({ height, width }: { height: number; width: number }) => (
                <FamilyTree
                  key={refreshKey}
                  householdId={householdId}
                  height={Math.max(MIN_HEIGHT, height)}
                  width={width}
                  unlinkedButtonPortalRef={unlinkedButtonPortalRef}
                  onEditClick={canEdit ? handleOpenEditTrowser : undefined}
                />
              )}
            </AutoSizer>
          </Box>
        </Fullscreen>
      )}
      {isEditTrowserOpen && (
        <EditFamilyTreeTrowser
          onClose={handleCloseEditTrowser}
          householdId={householdId}
        />
      )}
    </Stack>
  );
};

export function ClientFamilyTreePage() {
  const householdId = useRequiredParam('householdId');
  const { unlinkedButtonPortalRef } = useOutletContext<OutletContext>();
  return (
    <ClientFamilyTreeInner
      householdId={householdId}
      unlinkedButtonPortalRef={unlinkedButtonPortalRef}
    />
  );
}
